import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaMapMarkerAlt,
  FaCar,
  FaSearch,
  FaSpinner,
  FaClock,
} from "react-icons/fa";
// Make sure to add a background image for cars
import heroImage from "../../assets/react-cars-hero-bg.jpg";

const getFormattedDate = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fieldShell =
  "group relative flex min-h-[70px] lg:min-h-[76px] flex-col justify-center rounded-2xl md:rounded-[1.25rem] border border-white/60 bg-white/80 px-4 py-2 sm:py-3 shadow-sm transition-all duration-300 ease-out hover:bg-white hover:shadow-md focus-within:-translate-y-0.5 focus-within:border-[#2871d3] focus-within:bg-white focus-within:shadow-lg focus-within:ring-4 focus-within:ring-[#2871d3]/10";

const fieldLabel =
  "mb-0.5 sm:mb-1 flex items-center gap-1.5 text-[0.65rem] sm:text-[0.68rem] font-black uppercase tracking-[0.15em] text-slate-500 transition-colors group-focus-within:text-[#2871d3]";

const inputClass =
  "w-full min-w-0 bg-transparent text-base sm:text-lg font-extrabold text-slate-900 outline-none placeholder:font-semibold placeholder:text-slate-400";

export default function CarSearchWidget() {
  const navigate = useNavigate();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [sameLocation, setSameLocation] = useState(true);

  const [pickupDate, setPickupDate] = useState(getFormattedDate(today));
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropoffDate, setDropoffDate] = useState(getFormattedDate(tomorrow));
  const [dropoffTime, setDropoffTime] = useState("10:00");

  // Location Queries & Selection State
  const [pickupQuery, setPickupQuery] = useState("");
  const [pickupSelected, setPickupSelected] = useState(null);
  const [pickupResults, setPickupResults] = useState([]);
  const [showPickupDropdown, setShowPickupDropdown] = useState(false);
  const [loadingPickup, setLoadingPickup] = useState(false);

  const [dropoffQuery, setDropoffQuery] = useState("");
  const [dropoffSelected, setDropoffSelected] = useState(null);
  const [dropoffResults, setDropoffResults] = useState([]);
  const [showDropoffDropdown, setShowDropoffDropdown] = useState(false);
  const [loadingDropoff, setLoadingDropoff] = useState(false);

  const pickupRef = useRef(null);
  const dropoffRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickupRef.current && !pickupRef.current.contains(event.target)) {
        setShowPickupDropdown(false);
      }
      if (dropoffRef.current && !dropoffRef.current.contains(event.target)) {
        setShowDropoffDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch logic for places (Airports, Cities, etc.)
  const fetchSuggestions = useCallback(
    async (query, setResults, setLoading, selectedNode) => {
      if (!query || query.trim().length < 2) {
        return setResults([]);
      }

      // Prevent re-fetching if the query matches the currently selected display string
      if (
        selectedNode &&
        query ===
          `${selectedNode.name} (${selectedNode.iata_code || selectedNode.id})`
      ) {
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://cheapoflyhub-api.mohitbajaj1995.workers.dev/places/search?query=${encodeURIComponent(query)}`,
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setResults(data.data || []);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Debounced fetch for Pickup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showPickupDropdown && pickupQuery.length >= 2) {
        fetchSuggestions(
          pickupQuery,
          setPickupResults,
          setLoadingPickup,
          pickupSelected,
        );
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchSuggestions, pickupQuery, showPickupDropdown, pickupSelected]);

  // Debounced fetch for Dropoff
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showDropoffDropdown && dropoffQuery.length >= 2) {
        fetchSuggestions(
          dropoffQuery,
          setDropoffResults,
          setLoadingDropoff,
          dropoffSelected,
        );
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [dropoffQuery, fetchSuggestions, showDropoffDropdown, dropoffSelected]);

  const handleSelectPlace = (place, isPickup) => {
    const displayValue = place.iata_code
      ? `${place.name} (${place.iata_code})`
      : place.name;

    if (isPickup) {
      setPickupQuery(displayValue);
      setPickupSelected(place);
      setShowPickupDropdown(false);
    } else {
      setDropoffQuery(displayValue);
      setDropoffSelected(place);
      setShowDropoffDropdown(false);
    }
  };

  const handleSearchCars = () => {
    if (!pickupQuery || pickupQuery.trim() === "") {
      alert("Please enter a valid pick-up location.");
      return;
    }
    if (!sameLocation && (!dropoffQuery || dropoffQuery.trim() === "")) {
      alert("Please enter a valid drop-off location.");
      return;
    }

    const params = new URLSearchParams({
      pickup: pickupSelected
        ? pickupSelected.iata_code || pickupSelected.name
        : pickupQuery,
      pickupName: pickupSelected ? pickupSelected.name : pickupQuery,
      dropoff: sameLocation
        ? pickupSelected
          ? pickupSelected.iata_code || pickupSelected.name
          : pickupQuery
        : dropoffSelected
          ? dropoffSelected.iata_code || dropoffSelected.name
          : dropoffQuery,
      dropoffName: sameLocation
        ? pickupSelected
          ? pickupSelected.name
          : pickupQuery
        : dropoffSelected
          ? dropoffSelected.name
          : dropoffQuery,
      pickupDate,
      pickupTime,
      dropoffDate,
      dropoffTime,
    });

    navigate(`/cars?${params.toString()}`);
  };

  const renderDropdownList = (results, isLoading, isPickup) => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-3 p-6 text-sm font-semibold text-slate-500">
          <FaSpinner className="h-4 w-4 animate-spin text-[#2871d3]" />
          Searching locations...
        </div>
      );
    }

    if (!results || results.length === 0) {
      return (
        <div className="bg-slate-50/80 p-6 text-center text-sm font-medium text-slate-500">
          No locations found. Try another city or airport.
        </div>
      );
    }

    return (
      <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
        {results.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => handleSelectPlace(item, isPickup)}
            className="group flex w-full items-center justify-between gap-4 px-4 py-3 text-left transition-colors hover:bg-blue-50/80 sm:px-5"
          >
            <div className="min-w-0">
              <div className="mb-1 flex min-w-0 items-center gap-2">
                <span className="shrink-0 rounded-full bg-blue-100/50 px-2 py-0.5 text-[0.62rem] font-black uppercase tracking-wide text-[#2871d3]">
                  {item.type}
                </span>
                <span className="truncate text-sm font-extrabold text-slate-900">
                  {item.name}
                </span>
              </div>
              {item.city_name && (
                <div className="flex items-center gap-1.5 truncate text-xs font-medium text-slate-500">
                  <FaMapMarkerAlt className="h-3 w-3 shrink-0 text-slate-300" />
                  <span className="truncate">
                    {item.city_name}
                    {item.country_code ? `, ${item.country_code}` : ""}
                  </span>
                </div>
              )}
            </div>
            {item.iata_code && (
              <span className="shrink-0 rounded-xl bg-slate-100/80 px-2.5 py-1 font-mono text-sm font-black text-slate-500 transition-colors group-hover:bg-[#2871d3] group-hover:text-white shadow-sm">
                {item.iata_code}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  const PlaceDropdown = ({ show, results, loading, isPickup }) => (
    <div
      className={`absolute left-0 right-0 top-[calc(100%+8px)] z-[80] overflow-hidden rounded-2xl border border-slate-100 bg-white/95 backdrop-blur-xl shadow-2xl shadow-slate-900/10 ring-1 ring-slate-900/5 transition-all duration-200 origin-top ${
        show
          ? "opacity-100 scale-y-100 pointer-events-auto"
          : "opacity-0 scale-y-95 pointer-events-none"
      }`}
    >
      {renderDropdownList(results, loading, isPickup)}
    </div>
  );

  return (
    <section className="relative z-30 isolate overflow-visible font-sans bg-slate-50">
      <div className="absolute inset-0 -z-10 h-[85%] lg:h-full overflow-hidden">
        <img
          src={heroImage}
          alt="Car driving on scenic highway"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-50 via-slate-900/20 to-slate-900/60" />
      </div>

      <div className="mx-auto flex min-h-[100dvh] lg:min-h-[750px] w-full max-w-[1400px] flex-col justify-end px-4 pb-12 pt-12 sm:px-6 sm:pb-16 lg:px-8">
        <div className="mb-8 max-w-3xl text-white md:mb-10 lg:px-2">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-white backdrop-blur-md border border-white/30 shadow-sm">
            <FaCar className="h-3 w-3" /> US Car Rental Platform
          </span>
          <h1 className="mt-2 text-4xl font-black leading-[1.1] tracking-tight sm:text-5xl lg:text-7xl drop-shadow-md">
            Hit the road with <br className="hidden lg:block" /> top brands.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium leading-relaxed text-white/90 sm:text-lg drop-shadow-sm">
            Compare premium fleets, transparent pricing, and flexible pick-up
            locations across the country.
          </p>
        </div>

        <div className="rounded-[1.5rem] border border-white/40 bg-white/60 p-3 sm:p-5 lg:p-6 shadow-[0_30px_80px_rgba(0,0,0,0.12)] backdrop-blur-2xl sm:rounded-[2rem]">
          <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-start">
            <label className="group flex min-h-[44px] cursor-pointer items-center justify-between gap-3 rounded-full border border-white/60 bg-white/80 px-5 text-sm font-bold text-slate-700 shadow-sm transition-all hover:bg-white hover:text-slate-900 sm:justify-start">
              <span>Return to same location</span>
              <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                <input
                  type="checkbox"
                  checked={sameLocation}
                  onChange={(e) => setSameLocation(e.target.checked)}
                  className="peer h-5 w-5 cursor-pointer appearance-none rounded-[0.4rem] border-2 border-slate-300 bg-white/50 transition-all checked:border-[#2871d3] checked:bg-[#2871d3] focus:outline-none focus:ring-4 focus:ring-[#2871d3]/20"
                />
                <FaCheck className="pointer-events-none absolute h-2.5 w-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100" />
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-4">
            {/* Locations Row */}
            <div
              className={`relative flex flex-col gap-2 sm:grid ${sameLocation ? "sm:grid-cols-1" : "sm:grid-cols-2"} lg:col-span-12 lg:gap-4`}
            >
              <div className="relative w-full" ref={pickupRef}>
                <div className={fieldShell}>
                  <label className={fieldLabel}>
                    <FaMapMarkerAlt className="text-[#2871d3]" /> Pick-up
                    Location
                  </label>
                  <input
                    type="text"
                    value={pickupQuery}
                    onChange={(e) => setPickupQuery(e.target.value)}
                    onFocus={() => setShowPickupDropdown(true)}
                    placeholder="City, Airport, or Address"
                    className={`${inputClass} truncate`}
                  />
                </div>
                <PlaceDropdown
                  show={showPickupDropdown}
                  results={pickupResults}
                  loading={loadingPickup}
                  isPickup={true}
                />
              </div>

              {!sameLocation && (
                <div className="relative w-full" ref={dropoffRef}>
                  <div className={fieldShell}>
                    <label className={fieldLabel}>
                      <FaMapMarkerAlt className="text-[#2871d3]" /> Drop-off
                      Location
                    </label>
                    <input
                      type="text"
                      value={dropoffQuery}
                      onChange={(e) => setDropoffQuery(e.target.value)}
                      onFocus={() => setShowDropoffDropdown(true)}
                      placeholder="City, Airport, or Address"
                      className={`${inputClass} truncate`}
                    />
                  </div>
                  <PlaceDropdown
                    show={showDropoffDropdown}
                    results={dropoffResults}
                    loading={loadingDropoff}
                    isPickup={false}
                  />
                </div>
              )}
            </div>

            {/* Dates & Times Row */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:col-span-12 lg:grid-cols-[1.5fr_1fr_1.5fr_1fr_auto] lg:gap-4">
              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaCalendarAlt className="text-[#2871d3]" /> Pick-up Date
                </label>
                <input
                  type="date"
                  min={getFormattedDate(today)}
                  value={pickupDate}
                  onChange={(e) => setPickupDate(e.target.value)}
                  className="w-full bg-transparent text-sm sm:text-base font-extrabold text-slate-900 outline-none [color-scheme:light]"
                />
              </div>

              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaClock className="text-[#2871d3]" /> Time
                </label>
                <input
                  type="time"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  className="w-full bg-transparent text-sm sm:text-base font-extrabold text-slate-900 outline-none [color-scheme:light]"
                />
              </div>

              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaCalendarAlt className="text-[#2871d3]" /> Drop-off Date
                </label>
                <input
                  type="date"
                  min={pickupDate}
                  value={dropoffDate}
                  onChange={(e) => setDropoffDate(e.target.value)}
                  className="w-full bg-transparent text-sm sm:text-base font-extrabold text-slate-900 outline-none [color-scheme:light]"
                />
              </div>

              <div className={fieldShell}>
                <label className={fieldLabel}>
                  <FaClock className="text-[#2871d3]" /> Time
                </label>
                <input
                  type="time"
                  value={dropoffTime}
                  onChange={(e) => setDropoffTime(e.target.value)}
                  className="w-full bg-transparent text-sm sm:text-base font-extrabold text-slate-900 outline-none [color-scheme:light]"
                />
              </div>

              <button
                type="button"
                onClick={handleSearchCars}
                className="col-span-2 group flex min-h-[60px] lg:min-h-[76px] w-full items-center justify-center gap-3 rounded-2xl md:rounded-[1.25rem] bg-[#2871d3] px-6 text-base lg:text-lg font-black text-white shadow-xl shadow-blue-600/20 transition-all duration-300 ease-out hover:-translate-y-1 hover:bg-[#1d5ab3] hover:shadow-2xl hover:shadow-blue-600/30 active:translate-y-0 lg:col-span-1 min-[1024px]:min-w-[140px]"
              >
                <FaSearch className="h-4 w-4 lg:h-5 lg:w-5 transition-transform duration-300 group-hover:scale-110" />
                <span>Search</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
